/**
 * Forge queries and mutations (TASK-0035).
 *
 * The balance is a *snapshot of a moving value*, so it is treated as fresh rather than cached hard: a co-pilot call
 * settles a charge right after it answers, and a panel that kept a stale balance for the default five minutes would
 * report a number the user can already see is wrong. The history page, by contrast, is immutable once written, so it
 * keeps the ordinary caching.
 *
 * The pricing mutation writes the response straight into the cache for the same reason the settings mutations do: the
 * server already returned the new table, and a refetch would add a round-trip and a flash of stale prices.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  adminForgeApi,
  adminProviderProfileApi,
  forgeApi,
  providerProfileApi,
  FORGE_LEDGER_DEFAULT_PAGE_SIZE,
  type UpdateForgePricingPayload,
} from '@/features/forge/api/forgeApi';
import { queryKeys } from '@/lib/queryKeys';
import type {
  CreateProviderProfilePayload,
  ForgeBalance,
  ForgePricing,
  UpdateProviderProfilePayload,
} from '@/types/api';

/**
 * Reads the Forge balance, lifetime usage and the most recent movements.
 *
 * @param enabled Whether to run the query at all; a panel that is collapsed passes `false` so the request is skipped.
 */
export function useForgeBalance(enabled = true) {
  return useQuery({
    queryKey: queryKeys.forgeBalance,
    enabled,
    queryFn: forgeApi.getBalance,
    // The balance moves with every call, so it is re-read whenever the panel is shown rather than trusted for minutes.
    staleTime: 0,
  });
}

/**
 * Reads one page of the Forge ledger.
 *
 * @param skip Rows to skip; the caller owns the offset because it is what the pager renders.
 * @param take Page size; defaults to the size the endpoint serves.
 * @param enabled Whether to run the query at all.
 */
export function useForgeLedger(skip = 0, take = FORGE_LEDGER_DEFAULT_PAGE_SIZE, enabled = true) {
  return useQuery({
    queryKey: queryKeys.forgeLedger(skip, take),
    enabled,
    queryFn: () => forgeApi.getLedger(skip, take),
    // Paging moves to a new key, which would otherwise render as an empty table for the length of a round trip. Keeping
    // the previous page on screen means the pager only ever swaps rows, and the count in the header does not blink to
    // zero while the next page is in flight.
    placeholderData: (previous) => previous,
  });
}

/**
 * Reads the effective Forge price table.
 *
 * @param enabled Whether to run the query at all; only an administrator may read it, so a non-administrator screen
 *   must not mount the query and collect a `403`.
 */
export function useForgePricing(enabled = true) {
  return useQuery({
    queryKey: queryKeys.forgePricing,
    enabled,
    queryFn: adminForgeApi.getPricing,
  });
}

/**
 * Replaces the Forge override table.
 *
 * The response is the new truth: it is written into the pricing cache so the editor renders exactly what the server
 * stored, and the caller's own balance is invalidated because a price change moves the *next* charge, not the past.
 */
export function useUpdateForgePricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateForgePricingPayload) => adminForgeApi.updatePricing(payload),
    onSuccess: (pricing: ForgePricing) => {
      queryClient.setQueryData(queryKeys.forgePricing, pricing);
      void queryClient.invalidateQueries({ queryKey: queryKeys.forgeBalance });
    },
  });
}

/** Re-reads the balance and the first page of the ledger, for a manual refresh action. */
export function useRefreshForge() {
  const queryClient = useQueryClient();

  return async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['users', 'me', 'forge'] });
  };
}

/**
 * Reads the platform provider profiles an administrator manages (ADR-0019).
 *
 * @param includeDeleted Whether soft-deleted profiles are included; the administrator panel passes `true` so it can show what
 *   was removed and still has pricing references.
 * @param enabled Whether to run the query at all; only an administrator may read it.
 */
export function useProviderProfiles(includeDeleted = false, enabled = true) {
  return useQuery({
    queryKey: queryKeys.providerProfiles(includeDeleted),
    enabled,
    queryFn: () => adminProviderProfileApi.list(includeDeleted),
  });
}

/**
 * Creates a provider profile.
 *
 * The response is the new truth, so it is written into the cache for the flag the panel is showing; the selectable list is
 * invalidated as well, because a new profile may appear in a user's picker immediately.
 */
export function useCreateProviderProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateProviderProfilePayload) => adminProviderProfileApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'provider-profiles'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.selectableProviderProfiles });
    },
  });
}

/** Edits a provider profile; an omitted key keeps the stored credential. */
export function useUpdateProviderProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      profileId,
      payload,
    }: {
      profileId: string;
      payload: UpdateProviderProfilePayload;
    }) => adminProviderProfileApi.update(profileId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'provider-profiles'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.selectableProviderProfiles });
    },
  });
}

/** Enables or disables a provider profile without touching its credential. */
export function useSetProviderProfileState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ profileId, isActive }: { profileId: string; isActive: boolean }) =>
      adminProviderProfileApi.setState(profileId, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'provider-profiles'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.selectableProviderProfiles });
    },
  });
}

/** Soft-deletes a provider profile, so its pricing and audit references keep resolving. */
export function useDeleteProviderProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profileId: string) => adminProviderProfileApi.remove(profileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'provider-profiles'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.selectableProviderProfiles });
    },
  });
}

/**
 * Reads the platform profiles a user may select (ADR-0019 §D3).
 *
 * @param enabled Whether to run the query at all.
 */
export function useSelectableProviderProfiles(enabled = true) {
  return useQuery({
    queryKey: queryKeys.selectableProviderProfiles,
    enabled,
    queryFn: providerProfileApi.listSelectable,
    // The list only changes when an administrator edits a profile, so the ordinary caching is right for it.
    staleTime: 5 * 60 * 1000,
  });
}

/** Narrowing helper so a caller can hold the balance without repeating the query's type. */
export type { ForgeBalance };
